 
 
 

import React from "react";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../redux/initialStoreState";
import { useDispatchWrapper,
} from "../../../../../hooks";
import { PageLoader } from "../../../../../components/PageLoader/PageLoader";
import { LoadState } from "../../../../../constants/enums";
import { useNavigate, useOutletContext } from "react-router-dom";
import styled from "@emotion/styled";
import { alpha, Button, Menu, MenuItem, MenuProps, Stack } from "@mui/material";
import { PageTitleBar } from "../../../../../components/PageTitleBar/PageTitleBar";
import { useDataTableV2Pagination } from "../../../../../components/TableV2/hooks/useDataTableV2Pagination";
import { useTableV2State } from "../../../../../components/TableV2/hooks/useTableV2State";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { DataTableV2 } from "../../../../../components/TableV2/DataTableV2";
import {
  DataTableV2RowRenderType,
  IDataTableV2Props,
} from "../../../../../components/TableV2/interfaces/IDataTableV2Props";
import { formatText } from "../../../../../helpers";
import { StatusRenderer } from "../../../../../components/ActiveInActiveStatus/ActiveInActiveStatus";
import { ControlledCustomSelect } from "../../../../../components/formsComponents";
import { StandardTableActions } from "../../../../../components/StandardTableActions/StandardTableActions";
import {
  downloadCommAutomobileAlbertaPDFApplications,
  downloadCommAutomobilePDFApplications,
  downloadCommAutomobilePDFApplicationsV2,
  downloadCommAutomobileSupplementaryFormPDFAsync,
  fetchCommAutoAllApplicationsListAsync,
  previewCommAutomobileAlbertaPDFApplications,
  previewCommAutomobilePDFApplications,
  previewCommAutomobilePDFApplicationsV2,
  previewCommAutomobileSupplementaryFormPDFAsync,
} from "../../../../../redux/commAutoPolicy/commAutoAllApplications/commAutoAllApplicationsActions";
import {
  ICommAutoApplicationList,
  ICommAutomobileQuickAplication,
  INITIAL_QUICK_APPLICATION,
} from "../../../../../redux/commAutoPolicy/commAutoAllApplications/commAutoAllApplications.types";
import { RequestQuote } from "@mui/icons-material";
import { QuickApplicationDialog } from "./CommAutoApplication/QuickApplication";
import {
  downloadCommAutomobileCVSPDF,
  previewCommAutomobileCVSPDF,
} from "../../../../../redux/commAutoPolicy/commAutoSupplement/commAutoSupplementActions";
import { PageContainer } from "../../../../../components/container/PageContainer";
import { useCommAutoScope } from "../../../../leads/ManageLead/LeadCommAutoScopeContext";
import { showMessage } from "../../../../../redux/messages/messagesActions";

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
  />
))(({ theme }: any) => ({
  "& .MuiPaper-root": {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color: "rgb(55, 65, 81)",
    boxShadow:
      "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
    "& .MuiMenu-list": {
      padding: "4px 0",
    },
    "& .MuiMenuItem-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      "&:active": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        ),
      },
    },
    ...theme.applyStyles("dark", {
      color: theme.palette.grey[300],
    }),
  },
}));

const APPLICATION_TYPES = [
  { label: "All Applications", value: "all_application" },
  { label: "Automobile (AB)", value: "automobile_ab" },
  { label: "Automobile (ON)", value: "automobile_on" },
  { label: "Garage (ON)", value: "garage_on" },
  // { label: "Garage Supplement", value: "garage_supplementary" },
  // {
  //   label: "Commercial Vehicle Supplement",
  //   value: "commercial_vehicle_supplement",
  // },
  // { label: "Automobile (AB)", value: "automobile_ab" },
  // { label: "Garage (AB)", value: "garage_ab" },
];

const DOWNLOAD_FOR = [
  "automobile_ab",
  "automobile_on",
  "garage_on",
  "garage_supplementary",
  "commercial_vehicle_supplement",
];

const PREVIEW_FOR = [
  "automobile_ab",
  "automobile_on",
  "garage_on",
  "garage_supplementary",
  "commercial_vehicle_supplement",
];

/**
 * Feature: lead-commercial-auto-risk-profile
 *
 * Safe wrapper around `useOutletContext` that returns `undefined` instead of
 * throwing when the root is not rendered under a React Router `<Outlet>` (which
 * is the case inside the Lead Comm Auto panel, since that panel is not a
 * route). In the customer-policy context the outlet tuple is
 * `[customerId, customerPolicyId]`. Mirrors the pattern used in `DriverRoot`.
 */
const useOutletContextSafe = (): unknown[] | undefined => {
  try {
    return useOutletContext<unknown[]>();
  } catch {
    return undefined;
  }
};

/**
 * Feature: lead-commercial-auto-risk-profile
 *
 * Optional in-panel callbacks supplied by the Manage Lead Commercial Auto
 * container. When present (lead scope), the "Create" menu items and per-row Edit
 * open the matching application editor in-panel via these callbacks instead of
 * navigating to a `/comm-automobile-policy/...` route. `applicationType` is one
 * of the `APPLICATION_TYPES` values (`automobile_ab`, `automobile_on`,
 * `garage_on`). When absent (customer-policy scope) the root keeps its existing
 * route navigation (non-regression).
 */
export interface ICommAutoApplicationRootProps {
  onCreate?: (applicationType: string) => void;
  onEdit?: (applicationType: string, incrementalNumber: number) => void;
  // Opens the in-panel Create-From-ARS-Quote flow (lead scope). When provided,
  // the ARS button routes here instead of navigating to the policy-only route.
  onCreateARS?: () => void;
}

export const CommAutoApplicationRoot: React.FC<ICommAutoApplicationRootProps> = (
  props
) => {
  const { onCreate, onEdit, onCreateARS } = props;
  // Keep reading the router outlet in the customer-policy context; in lead
  // scope this returns `undefined` (the panel is not a route) and the resolved
  // scope comes from the LeadCommAutoScopeProvider instead.
  const outlet = useOutletContextSafe();
  const outletCustomerId = outlet?.[0] as number | undefined;
  const outletCustomerPolicyId = outlet?.[1] as number | undefined;

  // Lead scope wins; else the route fallback verbatim (Requirements 5.2, 5.3,
  // 11.1, 16.1). In lead scope `customerPolicyId` resolves to `undefined` and
  // `leadId` carries the current lead key.
  const { customerId, customerPolicyId, leadId } = useCommAutoScope({
    customerId: outletCustomerId,
    customerPolicyId: outletCustomerPolicyId,
  });

  // Feature: lead-commercial-auto-risk-profile (Requirements 11.5, 11.6, 16.4)
  // `isLeadScope` is the single branch key for hiding/disabling every
  // Policy_Only_Feature (per-row PDF download/preview, Create-from-ARS-Quote,
  // the route-navigating Create menu, and the `policy_number` column). None of
  // these have a lead-scoped equivalent before a policy exists. In
  // customer-policy scope (`leadId` absent) `isLeadScope` is `false` and all
  // four behaviors are preserved byte-for-byte (Requirement 16.4).
  const isLeadScope = Boolean(leadId);
  //// const { getParm } = useQueryParms();
  // const tabValue = getParm("tab");
  // const selectedTab = tabValue ? Number(tabValue) : 0;
  const [downloading, setDownloading] = React.useState(false);
  const [openView, setOpenView] =
    React.useState<ICommAutomobileQuickAplication | null>(null);
  const { list, totalRecords, loading } = useSelector(
    (storeState: IStoreState) =>
      storeState.commAuto.allApplications.applicationsList
  );

  const { customerInfo } = useSelector(
    (storeState: IStoreState) => storeState.commAuto.basicDetails
  );

  const dispatch = useDispatchWrapper();
  const navigate = useNavigate();

  // Non-blocking notice surfaced if a disabled/hidden Policy_Only_Feature is
  // somehow reached in lead scope. Uses the app's snackbar convention so the
  // broker stays in the Applications tab without an unhandled error and without
  // any route change (Requirement 11.6).
  const notifyPolicyOnlyUnavailable = () => {
    dispatch(
      showMessage({
        type: "info",
        message: "This action is unavailable before a policy exists",
        displayAs: "snackbar",
      })
    );
  };
  const { tablePagination, onPageChange, onRowsPerPageChange } =
    useDataTableV2Pagination({
      pageNumber: 1,
      rowsInPerPage: 25,
      extraFetchFactor: 1,
    });
  const [policyFormRoot, setPolicyFormRoot] = React.useState("all_application");
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFormTypeClick = (value: string, incrementNumber?: any) => () => {
    setAnchorEl(null);
    // Lead scope: open the matching editor in-panel via the container callbacks
    // (no route change). `incrementNumber` present => edit an existing record,
    // otherwise create a new one. Only the route-backed types (automobile_ab,
    // automobile_on, garage_on) have an in-panel editor; anything else falls
    // back to the non-blocking notice.
    if (isLeadScope) {
      const inPanelTypes = ["automobile_ab", "automobile_on", "garage_on"];
      if (!inPanelTypes.includes(value)) {
        notifyPolicyOnlyUnavailable();
        return;
      }
      if (incrementNumber) {
        onEdit?.(value, Number(incrementNumber));
      } else {
        onCreate?.(value);
      }
      return;
    }
    let additionalparms = "";
    if (incrementNumber) {
      additionalparms = "/" + incrementNumber;
    }
    switch (value) {
         case "automobile_ab": {
        navigate(
          `/comm-automobile-policy/${customerId}/application-tab/${customerPolicyId}/alberta-application${additionalparms}`
        );
        return;
      }
      case "automobile_on": {
        navigate(
          `/comm-automobile-policy/${customerId}/application-tab/${customerPolicyId}/application${additionalparms}`
        );
        return;
      }
      case "garage_on": {
        navigate(
          `/comm-automobile-policy/${customerId}/application-tab/${customerPolicyId}/garage${additionalparms}`
        );
        return;
      }
      case "garage_supplementary": {
        navigate(
          `/comm-automobile-policy/${customerId}/application-tab/${customerPolicyId}/garage-supplement${additionalparms}`
        );
        return;
      }
      case "commercial_vehicle_supplement": {
        navigate(
          `/comm-automobile-policy/${customerId}/application-tab/${customerPolicyId}/csv-detail-information${additionalparms}`
        );
        return;
      }
    }
  };

  const handleDownload = (
    application_type: string,
    incemental_number: number,
    policy_period_effective_date?: string | null,
  ) => {
    // PDF download is a Policy_Only_Feature keyed by customer_policy_id; the row
    // action is not rendered in lead scope, and this guard ensures no
    // customer_policy_id-keyed PDF thunk can fire even if reached (Req 11.6).
    if (isLeadScope || customerPolicyId === undefined) {
      notifyPolicyOnlyUnavailable();
      return;
    }
    const useV2 =
      application_type === "automobile_on" &&
      policy_period_effective_date &&
      policy_period_effective_date >= "2026-07-01";

    switch (application_type) {
      case "automobile_ab": {
        dispatch(
          downloadCommAutomobileAlbertaPDFApplications(
            customerPolicyId,
            incemental_number
          )
        );
        return;
      }
      case "automobile_on": {
        if (useV2) {
          dispatch(
            downloadCommAutomobilePDFApplicationsV2(
              customerPolicyId,
              incemental_number
            )
          );
        } else {
          dispatch(
            downloadCommAutomobilePDFApplications(
              customerPolicyId,
              incemental_number
            )
          );
        }
        return;
      }
      case "garage_supplementary": {
        dispatch(
          downloadCommAutomobileSupplementaryFormPDFAsync(
            customerPolicyId,
            incemental_number
          )
        );
        return;
      }
      case "commercial_vehicle_supplement": {
        dispatch(
          downloadCommAutomobileCVSPDF(customerPolicyId, incemental_number)
        );
        return;
      }
    }
  };

  const handlePreview = (
    application_type: string,
    incemental_number: number,
    policy_period_effective_date?: string | null,
  ) => {
    // PDF preview is a Policy_Only_Feature keyed by customer_policy_id; the row
    // action is not rendered in lead scope, and this guard ensures no
    // customer_policy_id-keyed PDF thunk can fire even if reached (Req 11.6).
    if (isLeadScope || customerPolicyId === undefined) {
      notifyPolicyOnlyUnavailable();
      return;
    }
    const useV2 =
      application_type === "automobile_on" &&
      policy_period_effective_date &&
      policy_period_effective_date >= "2026-07-01";

    switch (application_type) {
      case "automobile_ab": {
        dispatch(
          previewCommAutomobileAlbertaPDFApplications(
            customerPolicyId,
            incemental_number
          )
        );
        return;
      }
      case "automobile_on": {
        if (useV2) {
          dispatch(
            previewCommAutomobilePDFApplicationsV2(
              customerPolicyId,
              incemental_number
            )
          );
        } else {
          dispatch(
            previewCommAutomobilePDFApplications(
              customerPolicyId,
              incemental_number
            )
          );
        }
        return;
      }
      case "garage_supplementary": {
        dispatch(
          previewCommAutomobileSupplementaryFormPDFAsync(
            customerPolicyId,
            incemental_number
          )
        );
        return;
      }
      case "commercial_vehicle_supplement": {
        dispatch(
          previewCommAutomobileCVSPDF(customerPolicyId, incemental_number)
        );
        return;
      }
    }
  };

  const {
    state: { dateState, searchState, tabs, columnsConfig,
      dense },
    setDateState,
    setSearchState,
    setSelectedTab,
    setColumnVisibility,
 
    onDenseToggle, } = useTableV2State({
    filtersInitialState: {
      defaultDateRange: "last90Days",
      selectedTab: "-1",
    },
  });

  // BACKEND DEPENDENCY: Lead-keyed applications require the applications list
  // endpoint to accept an optional `lead_id` in place of `customer_policy_id`.
  // The `leadId` arg below is forwarded to `fetchCommAutoAllApplicationsListAsync`
  // (which swaps the scope key via `buildListKey`). Until the backend supports
  // the lead-keyed query the lead-scoped list degrades gracefully to an empty
  // list; the Policy_Only_Feature controls (PDF row actions, Create-from-ARS,
  // route-navigating Create menu, policy_number column) are hidden in lead
  // scope and any stray activation surfaces the non-blocking
  // `notifyPolicyOnlyUnavailable` notice while keeping the broker in the tab
  // (Requirements 11.5, 11.6). The type filter, Quick Create, paging, and the
  // customer-policy path are all unchanged (Requirements 11.1, 11.2, 16.1, 16.4).
  const fetchList = () => {
    dispatch(
      fetchCommAutoAllApplicationsListAsync(
        customerPolicyId as number,
        policyFormRoot,
        tablePagination.pageNumber,
        tablePagination.rowsInPerPage,
        leadId
      )
    );
  };

  const handleARSOpen = () => {
    // Lead scope: open the ARS quote flow in-panel via the container callback
    // (the created quote is scoped by `lead_id`). If no callback is wired, fall
    // back to the non-blocking notice rather than navigating to a policy-only
    // route. Customer-policy scope keeps the existing route navigation.
    if (isLeadScope) {
      if (onCreateARS) {
        onCreateARS();
      } else {
        notifyPolicyOnlyUnavailable();
      }
      return;
    }
    navigate(
      `/comm-automobile-policy/${customerId}/application-tab/${customerPolicyId}/quote-ars`
    );
  };

  const handlequickApplication = () => {
    fetchList();
    setOpenView(null);
  };

  const AutomobileApplicationDataProps: IDataTableV2Props = {
    isPagination: true,
    rowsPerPageOptions: tablePagination.rowsInPerPage,
    extraFetchFactor: tablePagination.extraFetchFactor,
    isDataLoading: loading === LoadState.InProgress,
    selectionMode: "none",
    uniqueRowKeyName: "incemental_number",
    tableCommandBarProps: {
      preDefinedPlugins: {
        columnVisibility: {
          columnVisibility: columnsConfig.columnVisibility,
          onChange: setColumnVisibility,
        },
        refresh: {
          onClick: fetchList,
        },
      },
      leftItems: {
        customPlugins: [
          {
            key: "status",
            onRender: () => (
              <ControlledCustomSelect
                sx={{ minWidth: "120px" }}
                value={policyFormRoot}
                options={APPLICATION_TYPES}
                onChange={(e) => setPolicyFormRoot(e.target.value as string)}
              ></ControlledCustomSelect>
            ),
          },
        ],
      },
      rightItems: {
        customPlugins: [],
      },
    },
    masterColumns: ([
      {
        key: "view",
        headerName: "Actions",
        fieldName: "",
        headingAlign: "center",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: ICommAutoApplicationList) => {
          return (
            <StandardTableActions
              onEditClick={handleFormTypeClick(
                row.application_type,
                row.incremental_number
              )}
              onDownLoadClick={
                !isLeadScope && DOWNLOAD_FOR.includes(row.application_type)
                  ? () =>
                      handleDownload(
                        row.application_type,
                        row.incremental_number,
                        row.policy_period_effective_date,
                      )
                  : undefined
              }
              onDownloadPreview={
                !isLeadScope && PREVIEW_FOR.includes(row.application_type)
                  ? () =>
                      handlePreview(
                        row.application_type,
                        row.incremental_number,
                        row.policy_period_effective_date,
                      )
                  : undefined
              }
            />
          );
        },
      },
      {
        key: "status",
        headerName: "Status",
        fieldName: "status",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: ICommAutoApplicationList) => {
          return <StatusRenderer status={row.status} />;
        },
      },
      {
        key: "incremental_number",
        headerName: "#",
        fieldName: "incremental_number",
        renderType: DataTableV2RowRenderType.TEXT_DARK,
      },
      {
        key: "policy_number",
        headerName: "Policy Number",
        fieldName: "policy_number",
        renderType: DataTableV2RowRenderType.TEXT_DARK,
      },
      {
        key: "policy_period_effective_date",
        headerName: "Effective Date",
        fieldName: "policy_period_effective_date",
        renderType: DataTableV2RowRenderType.DATE,
      },

      {
        key: "application_type",
        headerName: "Application Type",
        fieldName: "application_type",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        // exportCellWidth: 30,
        onRowCellValueRender: (value, row) => {
          return formatText(value).toUpperCase();
        },
      },
      {
        key: "create_ts",
        headerName: "Date Created",
        fieldName: "create_ts",
        renderType: DataTableV2RowRenderType.DATE,
        // exportCellWidth: 30,
      },
      {
        key: "created_by_name",
        headerName: "Created By",
        fieldName: "created_by_name",
        renderType: DataTableV2RowRenderType.TEXT_DARK,
        // exportCellWidth: 30,
      },
      // Feature: lead-commercial-auto-risk-profile (Requirement 11.5) — hide the
      // policy_number column in lead scope since no policy exists yet. The
      // `.filter` below is a no-op in customer-policy scope, preserving the
      // column byte-for-byte (Requirement 16.4).
    ] as IDataTableV2Props["masterColumns"]).filter(
      (col) => !(isLeadScope && col.key === "policy_number")
    ),
    initialSortConfig: {
      insert_ts: {
        direction: "desc",
        filedName: "create_ts",
      },
    },
    rows: list,
    onPageChange: onPageChange,
    onRowsPerPageChange: onRowsPerPageChange,
      dense: dense,
    onDenseToggle: onDenseToggle,};

  // Fetch on scope resolution: customer-policy scope keys on `customerPolicyId`
  // (unchanged); lead scope keys on `leadId`. Exactly one is present at a time.
  React.useEffect(() => {
    if (customerPolicyId || leadId) {
      fetchList();
    }
  }, [customerPolicyId, leadId]);

  return (
    <PageContainer title={"Applications"} description={"Applications"}>
      <PageLoader
        loading={customerInfo.loading === LoadState.InProgress}
        error={customerInfo.error ? { message: customerInfo.error } : null}
      >
        <PageTitleBar
          heading="Commercial Automobile Applications"
          rightHeading={
            <Stack direction={"row"} spacing={1}>
              <Button
                variant="contained"
                onClick={() => {
                  setOpenView({
                    ...INITIAL_QUICK_APPLICATION,
                  });
                }}
              >
                Quick Create
              </Button>
              {/*
                Feature: lead-commercial-auto-risk-profile (Requirement 11.5)
                Create-from-ARS-Quote and the route-navigating Create menu are
                Policy_Only_Features (they navigate to
                /comm-automobile-policy/... editor routes that require a saved
                customer policy) and are hidden in lead scope. Creation in lead
                scope uses the Quick Create dialog above, which accepts a lead
                key. In customer-policy scope (`!isLeadScope`) both controls
                render exactly as before (Requirement 16.4).
              */}
              {/*
                Feature: lead-commercial-auto-risk-profile
                "Create From ARS Quote" now renders in BOTH scopes. In
                customer-policy scope it navigates to the quote-ars route as
                before; in lead scope `handleARSOpen` opens the same ARS flow
                in-panel via `onCreateARS`, and the created quote is scoped by
                `lead_id`. The ARS form's branch/insurer/producer fields start
                blank and editable when opened from a lead.
              */}
              <Button
                variant="contained"
                disableElevation
                onClick={handleARSOpen}
                endIcon={<RequestQuote />}
              >
                Create From ARS Quote
              </Button>
              {/*
                The "Create" menu now renders in BOTH scopes. In customer-policy
                scope it navigates to the editor routes as before; in lead scope
                `handleFormTypeClick` opens the same editor in-panel via the
                container's `onCreate`/`onEdit` callbacks (no route change).
              */}
              <Button
                id="demo-customized-button"
                aria-controls={open ? "demo-customized-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                variant="contained"
                disableElevation
                onClick={handleClick}
                endIcon={<KeyboardArrowDownIcon />}
              >
                Create
              </Button>
              <StyledMenu
                id="demo-customized-menu"
                MenuListProps={{
                  "aria-labelledby": "demo-customized-button",
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
              >
                {APPLICATION_TYPES.slice(1).map((item, index) => {
                  return (
                    <MenuItem
                      key={index}
                      onClick={handleFormTypeClick(item.value)}
                      disableRipple
                    >
                      {item.label}
                    </MenuItem>
                  );
                })}
              </StyledMenu>
            </Stack>
          }
        />

        <DataTableV2 {...AutomobileApplicationDataProps} />
        {/* <TabsList
        sx={{ mb: 1 }}
        varaint="scrollable"
        selectedTab={selectedTab}
        tabs={[
          {
            label: "Automobile (AB)",
            icon: <AdminPanelSettings />,
          },
          {
            label: "Automobile (ON)",
            icon: <AdminPanelSettings />,
          },
          {
            label: "Garage (AB)",
            icon: <AccountCircle />,
          },
          {
            label: "Garage (ON)",
            icon: <AccountCircle />,
          },
          {
            label: "Garage Supplement",
            icon: <AccountCircle />,
          },
          {
            label: "Commercial Vehicle Supplement",
            icon: <AccountCircle />,
          },
        ]}
        onChange={handleChange}
      />
      <TabPanel value={selectedTab} index={1}>
        <CommAutomobileApplicationList />
      </TabPanel>
      <TabPanel value={selectedTab} index={3}>
      <CommAutoInsuranceGarageApplicationList />
      </TabPanel>
      <TabPanel value={selectedTab} index={4}>
      <CommAutoSupplementaryFormList
      customerId={customerId}
      customerPolicyId={customerPolicyId}
      />
      </TabPanel>
      <TabPanel value={selectedTab} index={5}>
      <CommAutomobileCSVList />
      </TabPanel> */}

        {openView && (
          <QuickApplicationDialog
            open={true}
            customerPolicyId={customerPolicyId as number}
            customerId={customerId as number}
            leadId={leadId}
            type={"commauto"}
            data={openView}
            onClose={() => setOpenView(null)}
            onComplete={handlequickApplication}
          />
        )}
      </PageLoader>
    </PageContainer>
  );
};
